import PropTypes from "prop-types";
// eslint-disable-next-line no-unused-vars
import * as React from "react";
import { memo, useCallback } from "react";
import { ficheProduitPropTypes } from "app/utils/propTypes.js";
import { QuotationPriceDetailContainer } from "app/pages/Booking/Quotation/PriceDetails/QuotationPriceDetailContainer.jsx";
import { PRODUCT_PAGE_CODE_STATUS } from "app/constants";
import ContactUs from "app/pages/.shared/ContactUs/ContactUs";
import IconBookingAsideLoader from "app/pages/.shared/IconBookingAsideLoader";
import CondensedBookWithConfidence from "app/pages/.shared/BookWithConfidence/CondensedBookWithConfidence";
import HolidaySummaryContainer from "app/pages/Booking/HolidaySummary/HolidaySummary/HolidaySummaryContainer";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";
import { FormattedMessage } from "react-intl";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import "./BookingAside.scss";
import PaymentDescriptionTermsContainer from "app/pages/Booking/Payment/PaymentDescriptionTerms/PaymentDescriptionTermsContainer";
import Separator from "app/pages/.shared/Separator";
import PayNowNote from "app/pages/.shared/PayNow/PayNowNote";

const BookingAside = ({
	shop = "",
	ficheProduit = {},
	showPaymentDescriptionTerms,
	showQuotationCTAButton = false,
	showContactUs = false,
	showPriceDetails = false,
	showProductPicture = false,
	depositPercentage,
	isSubmitting = false,
	isQuoteLoading = false,
	isBookingEligibleForDeposit,
	isBookingDateEligibleForDeposit,
	enableDepositDisplay = false,
	availablePaymentMethods = [],
	availablePaymentTerms = [],
	total,
}) => {
	const [, countryCode] = shop.split("-");

	const handleCTAClick = useCallback(() => {
		const quotationCta = document.querySelector("#quotation-cta");
		if (quotationCta) {
			quotationCta.click();
		}
	}, []);

	const isCTALoading = isSubmitting || isQuoteLoading;

	const payNowNote = total > 0 && (
		<PayNowNote
			total={total}
			depositPercentage={depositPercentage}
			availablePaymentMethods={availablePaymentMethods}
			displayAmountSuffix={false}
		/>
	);

	let paymentDescriptionTermsContent;
	if (isBookingEligibleForDeposit) {
		paymentDescriptionTermsContent = payNowNote;
	} else {
		paymentDescriptionTermsContent = <PaymentDescriptionTermsContainer />;
	}
	const displayPaymentTermsContent =
		showPaymentDescriptionTerms &&
		availablePaymentTerms?.length > 0 &&
		(enableDepositDisplay ? isBookingDateEligibleForDeposit : true);

	return (
		<div className="booking-aside">
			{ficheProduit.status === PRODUCT_PAGE_CODE_STATUS.SUCCESS ? (
				<div className="booking-aside__panel booking-aside__panel--my-booking">
					<HolidaySummaryContainer
						showBookingBanner
						expandProductDetails
						showProductPicture={showProductPicture}
					/>
				</div>
			) : (
				<IconBookingAsideLoader />
			)}

			<div style={{ position: "sticky", top: "0" }}>
				<div className="booking-aside__panel booking-aside__panel--price-detail">
					{showPriceDetails && !isQuoteLoading && <QuotationPriceDetailContainer />}

					{showQuotationCTAButton && (
						<>
							<Separator design="middle" />
							<Button
								variant={BUTTON_VARIANTS.FILLED}
								size={BUTTON_SIZES.LARGE}
								alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
								label={<FormattedMessage id="quotation.button.cta" />}
								icon={<IconRight />}
								onClick={handleCTAClick}
								disabled={isCTALoading}
								loading={isCTALoading}
							/>
						</>
					)}

					{displayPaymentTermsContent && <>{paymentDescriptionTermsContent}</>}
				</div>

				{countryCode === "GB" && (
					<div className="booking-aside__atol-abta">
						<CondensedBookWithConfidence />
					</div>
				)}

				{showContactUs && (
					<div className="booking-aside__panel booking-aside__panel--contact">
						<ContactUs productReference={ficheProduit.id} />
					</div>
				)}
			</div>
		</div>
	);
};

BookingAside.propTypes = {
	isBookingEligibleForDeposit: PropTypes.bool,
	shop: PropTypes.string,
	ficheProduit: ficheProduitPropTypes,
	showPaymentDescriptionTerms: PropTypes.bool,
	showQuotationCTAButton: PropTypes.bool,
	showContactUs: PropTypes.bool,
	showPriceDetails: PropTypes.bool,
	showProductPicture: PropTypes.bool,
	depositPercentage: PropTypes.number,
	isSubmitting: PropTypes.bool,
	isQuoteLoading: PropTypes.bool,
	availablePaymentTerms: PropTypes.array,
	availablePaymentMethods: PropTypes.array,
	isBookingDateEligibleForDeposit: PropTypes.bool,
	enableDepositDisplay: PropTypes.bool,
	total: PropTypes.number,
};

export default memo(BookingAside);
